"use client";

import { useRouter } from "next/navigation";
import {
	type ElementType,
	type JSX,
	type JSXElementConstructor,
	type PropsWithChildren,
	useRef,
} from "react";
import { useLink } from "react-aria";
import { RouterProvider } from "react-aria-components";
import { cnBase } from "tailwind-variants";

declare module "react-aria-components" {
	interface RouterConfig {
		routerOptions: NonNullable<
			Parameters<ReturnType<typeof useRouter>["push"]>[1]
		>;
	}
}

export function Router({ children }: PropsWithChildren) {
	const router = useRouter();

	return <RouterProvider navigate={router.push}>{children}</RouterProvider>;
}

type Props<T extends ElementType> = (T extends JSXElementConstructor<infer P>
	? P
	: T extends keyof JSX.IntrinsicElements
		? JSX.IntrinsicElements[T]
		: never) & { as?: T; className?: string };

export function Link<T extends ElementType = "a">({
	as = "a",
	className,
	children,
	href,
	...props
}: Props<T>) {
	const router = useRouter();
	const Component = as;
	const ref = useRef(null);
	const { linkProps } = useLink(
		{
			...props,
			href,
			onPress: (event) => {
				if (!event.metaKey && !event.ctrlKey) {
					router.push(href);
				}
			},
			elementType: as,
		},
		ref,
	);

	return (
		<Component
			className={cnBase("cursor-pointer", className)}
			{...linkProps}
			ref={ref}
		>
			{children}
		</Component>
	);
}
